import { useCallback, useEffect, useMemo, useState } from 'react';

import * as SituacaoAPI from '../../../../api/situacao';
import * as UsuarioAPI from '../../../../api/usuario';
import CrudsAPI from '../../../../api/CRUDs';

export function useHomologationRegistroModule({
    dados,
    setFlashMessage,
    pessoaId,
    empresaTabelaId,
    mrt,
    invalidateItemDetail,
    isAtive,
}) {
    const [rows, setRows] = useState([]);
    const [itens, setItens] = useState([]);
    const [itemSelecionado, setItemSelecionado] = useState('');

    const [selectedRow, setSelectedRow] = useState(null);

    const [openDialog, setOpenDialog] = useState(false);
    const [openDeleteDialog, setOpenDeleteDialog] = useState(false);
    const [openAssinarDialog, setOpenAssinarDialog] = useState(false);
    const [openRemoverAssinaturaDialog, setOpenRemoverAssinaturaDialog] = useState(false);
    const [registroAssinaturaSelecionado, setRegistroAssinaturaSelecionado] = useState(null);
    const [isAssinando, setIsAssinando] = useState(false);

    const [draft, setDraft] = useState({});

    const [optionsSituacao, setOptionsSituacao] = useState([]);
    const [optionsSituacaoFilter, setOptionsSituacaoFilter] = useState([]);

    const [isSaving, setIsSaving] = useState(false);

    const {
        columnFilterFns,
        columnFilters,
        globalFilter,
        sorting,
        pagination,

        isError,
        isLoading,
        isRefetching,
        setIsError,
        setIsLoading,
        setIsRefetching,
        rowCount,
        setRowCount,
    } = mrt;

    const carregarOptionsIniciais = useCallback(async () => {
        try {
            const resultSituacoes = (await SituacaoAPI.situacoes?.(empresaTabelaId)) || [];
            const filtrado = (resultSituacoes || []).filter((item) => item.homologationRegistro === true);

            const mapped = filtrado.map((item) => ({
                text: item.descricao,
                value: item.descricao,
            }));

            setOptionsSituacao(filtrado);
            setOptionsSituacaoFilter(mapped);
        } catch (error) {
            console.error(error);
        }
    }, [empresaTabelaId]);

    const carregarItens = useCallback(async () => {
        if (!dados?.id) return;

        try {
            const { data } = await CrudsAPI.homologationItem.listarObj({
                start: 0,
                size: 999,
                filters: JSON.stringify([]),
                filtersMode: JSON.stringify({}),
                globalFilter: '',
                sorting: JSON.stringify([]),
                homologationId: dados.id,
            });

            const lista = data?.data || [];
            setItens(lista);

            setItemSelecionado((prev) => {
                if (prev) return prev;
                return lista.length ? lista[0].id : '';
            });
        } catch (error) {
            console.error(error);
        }
    }, [dados?.id]);

    const fetchData = useCallback(async () => {
        if (!itemSelecionado) {
            setRows([]);
            setRowCount(0);
            return;
        }

        if (!rows.length) setIsLoading(true);
        else setIsRefetching(true);

        try {
            const filtros = [...(columnFilters || [])];

            filtros.push({
                id: 'itemId',
                value: itemSelecionado,
            });

            const filtrosMode = {
                ...(columnFilterFns || {}),
                itemId: 'equals',
            };

            const { data } = await CrudsAPI.homologationRegistro.listarObj({
                start: pagination.pageIndex * pagination.pageSize,
                size: pagination.pageSize,
                filters: JSON.stringify(filtros),
                filtersMode: JSON.stringify(filtrosMode),
                globalFilter: globalFilter ?? '',
                sorting: JSON.stringify(sorting ?? []),
                homologationId: dados?.id,
            });

            setRows(data.data || []);
            setRowCount(data.meta?.totalRowCount || 0);
            setIsError(false);
        } catch (error) {
            console.error(error);
            setIsError(true);
        } finally {
            setIsLoading(false);
            setIsRefetching(false);
        }
    }, [
        itemSelecionado,
        rows.length,
        columnFilters,
        columnFilterFns,
        globalFilter,
        sorting,
        pagination.pageIndex,
        pagination.pageSize,
        setIsError,
        setIsLoading,
        setIsRefetching,
        setRowCount,
    ]);

    const onEnterTab = useCallback(async () => {
        await carregarOptionsIniciais();
        await carregarItens();
    }, [carregarOptionsIniciais, carregarItens]);

    const handleOpenAssinarDialog = useCallback((registro) => {
        setRegistroAssinaturaSelecionado(registro);
        setOpenAssinarDialog(true);
    }, []);

    const handleCloseAssinarDialog = useCallback(() => {
        setRegistroAssinaturaSelecionado(null);
        setOpenAssinarDialog(false);
    }, []);

    const handleOpenRemoverAssinaturaDialog = useCallback((registro) => {
        setRegistroAssinaturaSelecionado(registro);
        setOpenRemoverAssinaturaDialog(true);
    }, []);

    const handleCloseRemoverAssinaturaDialog = useCallback(() => {
        setRegistroAssinaturaSelecionado(null);
        setOpenRemoverAssinaturaDialog(false);
    }, []);

    const handleAssinarRegistro = useCallback(
        async ({ usuario, senha }) => {
            try {
                if (!registroAssinaturaSelecionado?.id) return;

                setIsAssinando(true);

                const { data } = await UsuarioAPI.assinarHomologacao({
                    empresaId: dados.empresaId,
                    homologationId: dados.id,
                    itemId: registroAssinaturaSelecionado.itemId,
                    registroId: registroAssinaturaSelecionado.id,
                    tipo: 'registro',
                    usuario,
                    senha,
                });

                setFlashMessage(data.message, data.type);

                setOpenAssinarDialog(false);
                setRegistroAssinaturaSelecionado(null);

                await fetchData();

                if (invalidateItemDetail && registroAssinaturaSelecionado.itemId) {
                    invalidateItemDetail(registroAssinaturaSelecionado.itemId);
                }
            } catch (error) {
                console.error(error);
                setFlashMessage(error?.response?.data?.message || 'Erro ao assinar registro!', 'error');
            } finally {
                setIsAssinando(false);
            }
        },
        [dados?.empresaId, dados?.id, registroAssinaturaSelecionado, fetchData, setFlashMessage, invalidateItemDetail]
    );

    const handleRemoverAssinaturaRegistro = useCallback(
        async ({ usuario, senha }) => {
            try {
                const assinaturaUuid = registroAssinaturaSelecionado?.AssinaturaAtiva?.uuid;
                const itemId = registroAssinaturaSelecionado?.itemId;

                if (!assinaturaUuid) return;

                setIsAssinando(true);

                const { data } = await UsuarioAPI.removerAssinaturaHomologacao({
                    empresaId: dados.empresaId,
                    id: assinaturaUuid,
                    usuario,
                    senha,
                });

                setFlashMessage(data.message, data.type);

                setOpenRemoverAssinaturaDialog(false);
                setRegistroAssinaturaSelecionado(null);

                await fetchData();

                if (invalidateItemDetail && itemId) {
                    invalidateItemDetail(itemId);
                }
            } catch (error) {
                console.error(error);
                setFlashMessage(error?.response?.data?.message || 'Erro ao remover assinatura!', 'error');
            } finally {
                setIsAssinando(false);
            }
        },
        [dados?.empresaId, registroAssinaturaSelecionado, fetchData, setFlashMessage, invalidateItemDetail]
    );

    useEffect(() => {
        if (!isAtive) return;
        onEnterTab();
    }, [isAtive, onEnterTab]);

    useEffect(() => {
        if (!isAtive) return;
        fetchData();
    }, [isAtive, fetchData]);

    const handleAdicionar = useCallback(() => {
        if (!itemSelecionado) {
            setFlashMessage('Selecione um item da homologação primeiro!', 'warning');
            return;
        }

        setDraft({
            ativo: true,
            itemId: itemSelecionado,
            data: new Date().toISOString().substring(0, 10),
            prazo: new Date().toISOString().substring(0, 10),
        });

        setSelectedRow(null);
        setOpenDialog(true);
    }, [itemSelecionado, setFlashMessage]);

    const handleEditar = useCallback(
        (row) => {
            const obj = row.original;

            setSelectedRow(obj);
            setDraft({
                ...obj,
                itemId: obj.itemId || itemSelecionado,
                data: obj.data ? String(obj.data).substring(0, 10) : '',
                prazo: obj.prazo ? String(obj.prazo).substring(0, 10) : '',
            });

            setOpenDialog(true);
        },
        [itemSelecionado]
    );

    const handleSalvarDraft = useCallback(
        async (obj) => {
            if (isSaving) return;

            try {
                setIsSaving(true);

                let response;

                const payload = {
                    ...obj,
                    itemId: obj.itemId || itemSelecionado,
                };

                if (payload.uuid) response = await CrudsAPI.homologationRegistro.editar(payload);
                else response = await CrudsAPI.homologationRegistro.cadastrar(payload);

                setFlashMessage(response.data.message, response.data.type);

                setOpenDialog(false);
                setDraft({});
                setSelectedRow(null);

                await fetchData();

                if (invalidateItemDetail) {
                    invalidateItemDetail(payload.itemId);
                }
            } catch (error) {
                console.error(error);
                setFlashMessage(error?.response?.data?.message || 'Erro ao salvar registro!', 'error');
            } finally {
                setIsSaving(false);
            }
        },
        [itemSelecionado, fetchData, setFlashMessage, invalidateItemDetail, isSaving]
    );

    const handleDeletar = useCallback(async () => {
        try {
            if (!selectedRow?.uuid) return;

            const itemId = selectedRow.itemId;

            const { data } = await CrudsAPI.homologationRegistro.deletar({ id: selectedRow.uuid });

            setFlashMessage(data.message, data.type);

            setOpenDeleteDialog(false);
            setSelectedRow(null);

            await fetchData();

            if (invalidateItemDetail) {
                invalidateItemDetail(itemId);
            }
        } catch (error) {
            console.error(error);
            setFlashMessage(error?.response?.data?.message || 'Erro ao deletar registro!', 'error');
        }
    }, [selectedRow, fetchData, setFlashMessage, invalidateItemDetail]);

    const handleCancelar = useCallback(() => {
        setOpenDialog(false);
        setOpenDeleteDialog(false);
        setSelectedRow(null);
        setDraft({});
    }, []);

    const handleChangeItemSelecionado = useCallback((e) => {
        setItemSelecionado(e.target.value);
    }, []);

    return useMemo(
        () => ({
            state: {
                rows,
                itens,
                itemSelecionado,
                selectedRow,
                isSaving,
            },

            actions: {
                fetchData,
                carregarItens,
                carregarOptionsIniciais,
                onEnterTab,
                handleAdicionar,
                handleEditar,
                handleSalvarDraft,
                handleDeletar,
                handleCancelar,
                handleChangeItemSelecionado,
                setSelectedRow,
                setOpenDeleteDialog,
                handleAssinarRegistro,
                handleRemoverAssinaturaRegistro,
                handleOpenAssinarDialog,
                handleCloseAssinarDialog,
                handleOpenRemoverAssinaturaDialog,
                handleCloseRemoverAssinaturaDialog,
            },

            dialogs: {
                openDialog,
                setOpenDialog,
                openDeleteDialog,
                setOpenDeleteDialog,
                openAssinarDialog,
                setOpenAssinarDialog,
                openRemoverAssinaturaDialog,
                setOpenRemoverAssinaturaDialog,
                registroAssinaturaSelecionado,
                setRegistroAssinaturaSelecionado,
                isAssinando,
                draft,
                setDraft,
            },

            mrt: {
                ...mrt,
                isError,
                isLoading,
                isRefetching,
                rowCount,
            },

            options: {
                optionsSituacao,
                optionsSituacaoFilter,
            },

            context: {
                pessoaId,
            },
        }),
        [
            rows,
            itens,
            itemSelecionado,
            selectedRow,
            isSaving,
            fetchData,
            carregarItens,
            carregarOptionsIniciais,
            onEnterTab,
            handleAdicionar,
            handleEditar,
            handleSalvarDraft,
            handleDeletar,
            handleCancelar,
            handleChangeItemSelecionado,
            openDialog,
            openDeleteDialog,
            draft,
            mrt,
            isError,
            isLoading,
            isRefetching,
            rowCount,
            optionsSituacao,
            optionsSituacaoFilter,
            pessoaId,
            handleAssinarRegistro,
            handleRemoverAssinaturaRegistro,
        ]
    );
}
